import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { ConditionsSectionDTO } from '@lib/strapi/dtos/components/sections/conditionsSection.dto';
import { SectionHeading, dataOf } from '../shared-ui';
import { IconSpine } from '@components/utils/icons';

/**
 * Παθήσεις. `List` renders the pill wall (homepage); `Grid` the full cards
 * (/pathiseis). Pills over cards on the homepage because twelve names scan in
 * two lines — a visitor recognises their own condition faster in a word list
 * than a card wall.
 */
const ConditionsSectionImplementation = ({ section, page }: ComponentProps<ConditionsSectionDTO>) => {
  const { t } = useTranslation('main');
  if (!section) return null;
  const items = (dataOf(page).conditions ?? []) as { slug?: string; title?: string; teaser?: string }[];
  if (!items.length) return null;

  // With no section heading there is no h2 between the page h1 and the card
  // titles — heading-order violation (Lighthouse a11y, 2026-09-16; same rule
  // as StepsSection). Card titles take the level that follows what exists.
  const CardTitle = section.heading ? 'h3' : 'h2';
  return (
    <section className="my-14 md:my-20">
      <SectionHeading heading={section.heading} intro={section.intro} icon={<IconSpine />} />
      {section.layout === 'List' ? (
        <>
          <div className="flex flex-wrap gap-2.5">
            {items.map((c) => (
              <Link key={c.slug} href={`/${c.slug}`}
                className="rounded-full border border-violet-200 bg-violet-050 px-4 py-2 text-sm font-bold text-violet-700 transition-colors hover:border-violet-700 hover:bg-violet-200">
                {c.title}
              </Link>
            ))}
          </div>
          <div className="mt-6">
            <Link href="/pathiseis"
              className="inline-flex items-center gap-1.5 rounded-full border-2 border-violet-700 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white">
              {t('conditions.viewAll')}
            </Link>
          </div>
        </>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((c) => (
            <Link key={c.slug} href={`/${c.slug}`}
              className="group rounded-2xl border border-rule bg-white p-6 transition-all hover:-translate-y-0.5 hover:border-violet-400 hover:shadow-[0_14px_34px_-18px_rgba(102,0,204,0.5)]">
              <CardTitle className="text-lg font-extrabold text-ink group-hover:text-violet-700">{c.title}</CardTitle>
              {/* text-only cards: the teaser IS the content (the searcher's
                  «αυτό έχω εγώ» moment) — reading register, serif 16px,
                  unlike the image-led card grids (owner, 2026-09-10) */}
              {c.teaser ? <p className="mt-2 font-serif text-base text-body">{c.teaser}</p> : null}
              <span className="mt-4 inline-block text-sm font-extrabold text-violet-700">{t('conditions.more')}</span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
};

export default ConditionsSectionImplementation;
